import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Eye,
  EyeOff,
  ImagePlus,
  Link2,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { INPUT_CLASS } from '../../Scheduler/schedulerUi';
import {
  ModelAttribute,
  ModelDetail,
  ModelImage,
  errorText,
  useAttributeTypes,
  useCategories,
  useCatalogueWrite,
  useModel,
} from './api';
import { RowMenu, SourceBadge, attrLabel, fmtDay, useConfirm } from './ui';

type Tab = 'details' | 'specs' | 'photos' | 'listings';

const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-blue-400';

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <label className="block space-y-1">
    <span className="text-xs font-medium text-gray-600">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-400">{hint}</span>}
  </label>
);

/* ---------- details ---------- */

const DetailsTab: React.FC<{ model: ModelDetail }> = ({ model }) => {
  const categories = useCategories();
  const initial = useMemo(
    () => ({
      name: model.name || '',
      name_marketing: model.name_marketing || '',
      description_short: model.description_short || '',
      description: model.description || '',
      gtin: model.gtin || '',
      etid: String(model.etid || ''),
      private: model.private ? '1' : '0',
    }),
    [model]
  );
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  const changed = Object.fromEntries(
    Object.entries(form).filter(([k, v]) => v !== (initial as Record<string, string>)[k])
  );
  const save = useCatalogueWrite(
    (call, body: Record<string, unknown>) => call(`/${model.eid}`, 'PUT', body),
    'Model saved.'
  );
  const submit = () => {
    if (!form.name.trim()) return;
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(changed)) {
      body[k] = k === 'etid' || k === 'private' ? Number(v) : v;
    }
    save.mutate(body);
  };

  return (
    <div className="space-y-4">
      <Field label="Name" hint="What sellers see in the picker and the new listing's title.">
        <input
          className={`${INPUT_CLASS} w-full`}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>
      <Field label="Marketing name">
        <input
          className={`${INPUT_CLASS} w-full`}
          value={form.name_marketing}
          onChange={(e) => setForm({ ...form, name_marketing: e.target.value })}
        />
      </Field>
      <Field label="Category">
        <select
          className={`${INPUT_CLASS} w-full`}
          value={form.etid}
          disabled={categories.isLoading}
          onChange={(e) => setForm({ ...form, etid: e.target.value })}
        >
          {categories.isLoading && <option value={form.etid}>Loading categories…</option>}
          {!categories.data?.some((c) => String(c.etid) === form.etid) && (
            <option value={form.etid}>{model.category || `Type ${model.etid}`}</option>
          )}
          {(categories.data || []).map((c) => (
            <option key={c.etid} value={c.etid}>
              {c.path}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Short description" hint="Up to 200 characters; the picker's one-line summary.">
        <input
          className={`${INPUT_CLASS} w-full`}
          maxLength={200}
          value={form.description_short}
          onChange={(e) => setForm({ ...form, description_short: e.target.value })}
        />
      </Field>
      <Field label="Description" hint="Copied into every listing cloned from this model.">
        <textarea
          rows={6}
          className={TEXTAREA_CLASS}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Barcode (GTIN)">
          <input
            className={`${INPUT_CLASS} w-full`}
            value={form.gtin}
            onChange={(e) => setForm({ ...form, gtin: e.target.value })}
          />
        </Field>
        <Field label="Visibility" hint="Private models only show to their owner.">
          <select
            className={`${INPUT_CLASS} w-full`}
            value={form.private}
            onChange={(e) => setForm({ ...form, private: e.target.value })}
          >
            <option value="0">Public: every seller</option>
            <option value="1">Private: owner only</option>
          </select>
        </Field>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
        {Object.keys(changed).length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setForm(initial)} disabled={save.isPending}>
            Discard
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={!Object.keys(changed).length || !form.name.trim() || save.isPending}>
          {save.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
          Save changes
        </Button>
      </div>
    </div>
  );
};

/* ---------- specs ---------- */

const SpecRow: React.FC<{
  attr: ModelAttribute;
  eid: number;
  onDelete: (a: ModelAttribute) => void;
}> = ({ attr, eid, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(attr.content || '');
  useEffect(() => setValue(attr.content || ''), [attr.content]);
  const save = useCatalogueWrite(
    (call, content: string) =>
      call(`/${eid}/attributes`, 'PUT', { attributes: [{ eaid: attr.eaid, eatid: attr.eatid, content }] }),
    `${attrLabel(attr.name)} saved.`
  );
  return (
    <tr className="align-top">
      <td className="w-2/5 px-3 py-2 text-gray-600">
        {attrLabel(attr.name)}
        {attr.unit ? <span className="text-gray-400"> ({attr.unit})</span> : null}
      </td>
      <td className="px-3 py-2">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              className={`${INPUT_CLASS} w-full`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && value.trim()) save.mutate(value.trim(), { onSuccess: () => setEditing(false) });
                if (e.key === 'Escape') setEditing(false);
              }}
            />
            <button
              type="button"
              aria-label="Save"
              disabled={!value.trim() || save.isPending}
              onClick={() => save.mutate(value.trim(), { onSuccess: () => setEditing(false) })}
              className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
            >
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </button>
            <button type="button" aria-label="Cancel" onClick={() => setEditing(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <span className="break-words text-gray-900">{attr.content || <span className="text-gray-400">empty</span>}</span>
        )}
      </td>
      <td className="w-10 px-2 py-2 text-right">
        {!editing && (
          <RowMenu
            label={`Actions for ${attrLabel(attr.name)}`}
            entries={[
              { label: 'Edit value', icon: <Pencil className="h-4 w-4" />, onSelect: () => setEditing(true) },
              { label: 'Remove', icon: <Trash2 className="h-4 w-4" />, onSelect: () => onDelete(attr), danger: true, separatorBefore: true },
            ]}
          />
        )}
      </td>
    </tr>
  );
};

const AddSpec: React.FC<{ model: ModelDetail }> = ({ model }) => {
  const [q, setQ] = useState('');
  const [eatid, setEatid] = useState<number | null>(null);
  const [value, setValue] = useState('');
  const types = useAttributeTypes(model.etid, q);
  const have = new Set(model.attributes.map((a) => a.eatid));
  const add = useCatalogueWrite(
    (call, v: { eatid: number; content: string }) => call(`/${model.eid}/attributes`, 'POST', { attributes: [v] }),
    'Spec added.'
  );
  const chosen = types.data?.find((t) => t.eatid === eatid);
  return (
    <div className="space-y-2 rounded-lg border border-dashed border-gray-200 p-3">
      <p className="text-xs font-medium text-gray-600">Add a spec</p>
      <div className="flex flex-wrap gap-2">
        <input
          className={`${INPUT_CLASS} min-w-[10rem] flex-1`}
          placeholder="Find an attribute (e.g. battery, colour)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setEatid(null);
          }}
        />
        <select
          className={`${INPUT_CLASS} min-w-[12rem] flex-1`}
          value={eatid ?? ''}
          onChange={(e) => setEatid(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{types.isLoading ? 'Loading…' : 'Choose attribute'}</option>
          {(types.data || []).map((t) => (
            <option key={t.eatid} value={t.eatid}>
              {attrLabel(t.name)}
              {t.unit ? ` (${t.unit})` : ''}
              {have.has(t.eatid) ? ' - already set' : t.used_in_category ? ` - used on ${t.used_in_category}` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        <input
          className={`${INPUT_CLASS} flex-1`}
          placeholder={chosen ? `Value for ${attrLabel(chosen.name)}` : 'Value'}
          value={value}
          disabled={!eatid}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button
          size="sm"
          disabled={!eatid || !value.trim() || add.isPending}
          onClick={() =>
            add.mutate(
              { eatid: eatid as number, content: value.trim() },
              { onSuccess: () => { setValue(''); setEatid(null); setQ(''); } }
            )
          }
        >
          {add.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-2 h-3.5 w-3.5" />}
          Add
        </Button>
      </div>
    </div>
  );
};

const SpecsTab: React.FC<{ model: ModelDetail; ask: ReturnType<typeof useConfirm>['ask'] }> = ({ model, ask }) => {
  const remove = useCatalogueWrite(
    (call, a: ModelAttribute) => call(`/${model.eid}/attributes`, 'DELETE', { attributes: [{ eaid: a.eaid }] }),
    (a) => `${attrLabel(a.name)} removed.`
  );
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Specs are copied into every new listing cloned from this model. Listings already made keep their own copy.
      </p>
      <div className="overflow-hidden rounded-lg border border-gray-100">
        <table className="min-w-full divide-y divide-gray-50 text-sm">
          <tbody className="divide-y divide-gray-50">
            {model.attributes.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-center text-gray-400">No specs yet. Add the first one below.</td>
              </tr>
            ) : (
              model.attributes.map((a) => (
                <SpecRow
                  key={a.eaid}
                  attr={a}
                  eid={model.eid}
                  onDelete={(attr) =>
                    ask({
                      title: `Remove ${attrLabel(attr.name)}?`,
                      body: `"${attr.content}" will no longer be copied into new listings.`,
                      confirm: 'Remove',
                      danger: true,
                      run: () => remove.mutateAsync(attr),
                    })
                  }
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <AddSpec model={model} />
    </div>
  );
};

/* ---------- photos ---------- */

const PhotoCard: React.FC<{
  img: ModelImage;
  index: number;
  count: number;
  eid: number;
  onMove: (from: number, to: number) => void;
  onCover: () => void;
  onDelete: () => void;
}> = ({ img, index, count, eid, onMove, onCover, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [credit, setCredit] = useState(img.description || '');
  useEffect(() => setCredit(img.description || ''), [img.description]);
  const save = useCatalogueWrite(
    (call, description: string) => call(`/${eid}/assets/${img.asid}`, 'PUT', { description }),
    'Photo credit saved.'
  );
  return (
    <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
      <div className="relative aspect-square bg-gray-50">
        {img.url ? (
          <img src={img.url} alt="" loading="lazy" className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">No preview</div>
        )}
        {img.default ? (
          <span className="absolute left-2 top-2 rounded bg-blue-950 px-1.5 py-0.5 text-xs font-medium text-white">Cover</span>
        ) : null}
        <div className="absolute right-1 top-1 rounded-lg bg-white/90">
          <RowMenu
            label="Photo actions"
            entries={[
              { label: 'Make cover', icon: <Star className="h-4 w-4" />, onSelect: onCover, hidden: !!img.default },
              { label: 'Move earlier', icon: <ArrowLeft className="h-4 w-4" />, onSelect: () => onMove(index, index - 1), hidden: index === 0 },
              { label: 'Move later', icon: <ArrowRight className="h-4 w-4" />, onSelect: () => onMove(index, index + 1), hidden: index === count - 1 },
              { label: 'Edit credit', icon: <Pencil className="h-4 w-4" />, onSelect: () => setEditing(true) },
              { label: 'Open original', icon: <ExternalLink className="h-4 w-4" />, onSelect: () => img.url && window.open(img.url, '_blank', 'noopener'), hidden: !img.url },
              { label: 'Remove photo', icon: <Trash2 className="h-4 w-4" />, onSelect: onDelete, danger: true, separatorBefore: true },
            ]}
          />
        </div>
      </div>
      <div className="p-2">
        {editing ? (
          <div className="space-y-1">
            <textarea
              rows={3}
              className={TEXTAREA_CLASS}
              value={credit}
              placeholder="Photo: author, licence, via source. https://…"
              onChange={(e) => setCredit(e.target.value)}
            />
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(credit, { onSuccess: () => setEditing(false) })}>
                {save.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}Save
              </Button>
            </div>
          </div>
        ) : (
          <p className="line-clamp-3 break-words text-xs text-gray-500" title={img.description || ''}>
            {img.description || <span className="text-gray-400">No credit recorded</span>}
          </p>
        )}
      </div>
    </div>
  );
};

const PhotosTab: React.FC<{ model: ModelDetail; ask: ReturnType<typeof useConfirm>['ask'] }> = ({ model, ask }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [urls, setUrls] = useState('');
  const images = model.images;
  const upload = useCatalogueWrite(
    (call, files: File[]) => {
      const form = new FormData();
      files.forEach((f) => form.append('files', f, f.name));
      return call(`/${model.eid}/assets/upload`, 'POST', form);
    },
    (_v, res) => `${res?.attached ?? 0} photo(s) added${res?.failed?.length ? `, ${res.failed.length} refused` : ''}.`
  );
  const fromUrls = useCatalogueWrite(
    (call, list: string[]) => call(`/${model.eid}/assets/from-urls`, 'POST', { urls: list, max_images: 10 }),
    (_v, res) => `${res?.attached ?? 0} photo(s) added${res?.failed?.length ? `, ${res.failed.length} failed` : ''}.`
  );
  const reorder = useCatalogueWrite(
    (call, asids: number[]) => call(`/${model.eid}/assets/order`, 'PUT', { asids }),
    'Photo order saved.'
  );
  const cover = useCatalogueWrite(
    (call, asid: number) => call(`/${model.eid}/assets/${asid}/primary`, 'POST'),
    'Cover photo set.'
  );
  const remove = useCatalogueWrite(
    (call, asid: number) => call(`/${model.eid}/assets/${asid}`, 'DELETE'),
    'Photo removed.'
  );
  const move = (from: number, to: number) => {
    const order = images.map((i) => i.asid);
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    reorder.mutate(order);
  };
  const busy = upload.isPending || fromUrls.isPending;
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Only use photos that are royalty-free with no watermark, or that the brand owner gave us, and record the credit.
        New listings cloned from this model start with these photos.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length) upload.mutate(files);
            e.target.value = '';
          }}
        />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
          {upload.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-2 h-3.5 w-3.5" />}
          Upload photos
        </Button>
      </div>
      <div className="space-y-2 rounded-lg border border-dashed border-gray-200 p-3">
        <p className="text-xs font-medium text-gray-600">Add from links</p>
        <textarea
          rows={2}
          className={TEXTAREA_CLASS}
          placeholder="One https:// image link per line. Each is downloaded and re-hosted, never hotlinked."
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !urls.trim()}
            onClick={() =>
              fromUrls.mutate(
                urls.split(/\s+/).filter((u) => u.startsWith('https://')),
                { onSuccess: () => setUrls('') }
              )
            }
          >
            {fromUrls.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-2 h-3.5 w-3.5" />}
            Add links
          </Button>
        </div>
      </div>
      {images.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-gray-100 py-10 text-sm text-gray-400">
          <ImagePlus className="h-6 w-6" />
          No photos. Sellers cloning this model start with an empty gallery.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((img, i) => (
            <PhotoCard
              key={img.asid}
              img={img}
              index={i}
              count={images.length}
              eid={model.eid}
              onMove={move}
              onCover={() => cover.mutate(img.asid)}
              onDelete={() =>
                ask({
                  title: 'Remove this photo?',
                  body: 'New listings will no longer get it. Listings already made keep their own copy.',
                  confirm: 'Remove photo',
                  danger: true,
                  run: () => remove.mutateAsync(img.asid),
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- listings ---------- */

const ListingsTab: React.FC<{ model: ModelDetail }> = ({ model }) => (
  <div className="space-y-2">
    <p className="text-xs text-gray-500">
      {model.listings_total.toLocaleString()} listing(s) were made from this model
      {model.listings_total > model.listings.length ? `; the newest ${model.listings.length} are shown` : ''}.
    </p>
    <div className="overflow-hidden rounded-lg border border-gray-100">
      <table className="min-w-full divide-y divide-gray-50 text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="px-3 py-2 font-medium">Listing</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Store</th>
            <th className="px-3 py-2 font-medium">Added</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {model.listings.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-center text-gray-400">No seller has listed this model yet.</td>
            </tr>
          ) : (
            model.listings.map((l) => (
              <tr key={l.eid} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <a href={`/admin/listings/${l.eid}`} className="font-medium text-blue-900 hover:underline">
                    {l.name || `Listing ${l.eid}`}
                  </a>
                </td>
                <td className="px-3 py-2 text-gray-600">{l.status || '—'}</td>
                <td className="px-3 py-2 text-gray-600">
                  {l.stores?.length ? (
                    l.stores.map((s) => (
                      <a key={String(s)} href={`/admin/stores/${s}`} className="mr-2 text-blue-900 hover:underline">
                        Store {s}
                      </a>
                    ))
                  ) : (
                    <span className="text-gray-400">Not in a store</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500">{fmtDay(l.date_added)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ---------- the sheet ---------- */

export const ModelEditor: React.FC<{ eid: number | null; onClose: () => void }> = ({ eid, onClose }) => {
  const [tab, setTab] = useState<Tab>('details');
  const q = useModel(eid);
  const { ask, dialog } = useConfirm();
  useEffect(() => setTab('details'), [eid]);
  const model = q.data;

  const retire = useCatalogueWrite((call, id: number) => call(`/${id}`, 'DELETE'), 'Model retired. Sellers no longer see it.');
  const restore = useCatalogueWrite((call, id: number) => call(`/${id}/restore`, 'POST'), 'Model restored.');
  const visibility = useCatalogueWrite(
    (call, v: { id: number; private: number }) => call(`/${v.id}`, 'PUT', { private: v.private }),
    (v) => (v.private ? 'Model is now private.' : 'Model is now public.')
  );

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'details', label: 'Details' },
    { id: 'specs', label: 'Specs', count: model?.attributes.length },
    { id: 'photos', label: 'Photos', count: model?.images.length },
    { id: 'listings', label: 'Listings', count: model?.listings_total },
  ];

  return (
    <Sheet open={eid != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        {q.isLoading || !model ? (
          <div className="space-y-3 pt-6">
            {q.isError ? (
              <p className="text-sm text-red-600">{errorText(q.error, 'This model could not be loaded.')}</p>
            ) : (
              <>
                <p className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading model {eid}…
                </p>
                <Skeleton className="h-8 w-2/3" />
                <Skeleton className="h-40 w-full" />
              </>
            )}
          </div>
        ) : (
          <>
            <SheetHeader className="space-y-2 pr-8 text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle className="break-words">{model.name || `Model ${model.eid}`}</SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>{model.category || `Type ${model.etid}`}</span>
                      <SourceBadge source={model.source} isPrivate={!!model.private} />
                      {model.deleted ? <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">Retired</span> : null}
                      <span>eid {model.eid}</span>
                      <span>added {fmtDay(model.date_added)}</span>
                    </div>
                  </SheetDescription>
                </div>
                <RowMenu
                  label="Model actions"
                  entries={[
                    {
                      label: model.private ? 'Make public' : 'Make private',
                      icon: model.private ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />,
                      onSelect: () =>
                        ask({
                          title: model.private ? 'Make this model public?' : 'Make this model private?',
                          body: model.private
                            ? 'Every seller will be able to find and list it.'
                            : 'Only its owner will see it in the picker.',
                          confirm: model.private ? 'Make public' : 'Make private',
                          run: () => visibility.mutateAsync({ id: model.eid, private: model.private ? 0 : 1 }),
                        }),
                    },
                    {
                      label: 'Restore',
                      icon: <RotateCcw className="h-4 w-4" />,
                      hidden: !model.deleted,
                      onSelect: () =>
                        ask({
                          title: 'Restore this model?',
                          body: 'It goes back into the seller picker.',
                          confirm: 'Restore',
                          run: () => restore.mutateAsync(model.eid),
                        }),
                    },
                    {
                      label: 'Retire',
                      icon: <Trash2 className="h-4 w-4" />,
                      danger: true,
                      separatorBefore: true,
                      hidden: !!model.deleted,
                      onSelect: () =>
                        ask({
                          title: 'Retire this model?',
                          body: `Sellers will no longer find "${model.name}". The ${model.listings_total} listing(s) already made from it are not touched. You can restore it later.`,
                          confirm: 'Retire',
                          danger: true,
                          run: () => retire.mutateAsync(model.eid),
                        }),
                    },
                  ]}
                />
              </div>
            </SheetHeader>

            <div className="mt-4 flex gap-1 border-b border-gray-100">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                    tab === t.id ? 'border-blue-900 font-medium text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {t.label}
                  {t.count != null && <span className="ml-1 text-xs text-gray-400">{t.count}</span>}
                </button>
              ))}
            </div>
            <div className="py-4">
              {tab === 'details' && <DetailsTab model={model} />}
              {tab === 'specs' && <SpecsTab model={model} ask={ask} />}
              {tab === 'photos' && <PhotosTab model={model} ask={ask} />}
              {tab === 'listings' && <ListingsTab model={model} />}
            </div>
          </>
        )}
        {dialog}
      </SheetContent>
    </Sheet>
  );
};
